<template>
  <div :key="`simcisionmodel-${loadedModel?.id || 'none'}`" class="app-editor" :class="{ 'is-fullscreen': isFullscreen }">
    <template v-if="loadedModel?.id">
      <editor-navigation v-if="!isFullscreen" class="app-editor__navigation" />
      <div v-else></div>

      <div class="app-editor__view">
        <router-view></router-view>
        <project-settings />
        <language-settings />
      </div>

      <info-panel-selected />

      <!-- POPUPS -->
      <save-popup />
    </template>
    <template v-else>
      <div></div>
      <error-page-content
        :title="loadingError?.message ?? $t('errors.model_not_found')"
        :status="loadingError?.status"
        :error-code="loadingError?.code"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as API from "@/api/API";
import eventHub from "@/helpers/utility/Events";

import { useUnloadGuard } from "@/helpers/data/UnloadGuard.js";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { startPresencePersistence, stopPresencePersistence } from "@editor/store/presencePersistence";

import EditorNavigation from "@editor/components/EditorNavigation.vue";

import InfoPanelSelected from "@editor/ui/InfoPanelSelected.vue";

// settings
import ProjectSettings from "@editor/components/ProjectSettings.vue";
import LanguageSettings from "@editor/components/LanguageSettings.vue";

import SavePopup from "@core/ui/SavePopup/SavePopup.vue";
import ErrorPageContent from "@core/errors/ErrorPageContent.vue";
import { useSimulationAPI } from "./views/simulation/simulationAPI";

const router = useRouter();
const route = useRoute();
const { loadedModel, loadingError, setEmbedded } = useLoadedModel();
const { addUnloadGuardListener } = useUnloadGuard();
addUnloadGuardListener();

// ==============================
// FULLSCREEN

const isFullscreen = ref(document.fullscreenElement !== null);

function updateFullscreen() {
  isFullscreen.value = document.fullscreenElement !== null;
}
onMounted(() => {
  document.addEventListener("fullscreenchange", updateFullscreen);
  const pageInIframe = window.self !== window.top;
  if (pageInIframe) {
    setEmbedded(true);
  } else {
    setEmbedded(false);
  }

  if (route.query.simulationLocked === "true") {
    loadedModel.simulationLocked = true;
  }
  if (route.query.simulationControlsHidden === "true") {
    loadedModel.simulationControlsHidden = true;
  }

  startPresencePersistence();
});
onBeforeUnmount(() => {
  document.removeEventListener("fullscreenchange", updateFullscreen);
  stopPresencePersistence();
});

// ==============================
// TOKEN REFRESH

async function refreshToken() {
  // If we have no valid token, try refreshing it.
  if (API.isTokenExpired()) {
    try {
      return await API.getNewToken(false);
    } catch (_error) {
      const error = _error as { status?: number; message: string };

      if (error.status == null) {
        console.warn("Login failed (no connection)");
      } else {
        console.warn("Login failed.", error.status, error.message);
      }
    }
  }
}
refreshToken();

// ==============================
// SIMULATION API

useSimulationAPI();

// ==============================
// ROUTER

eventHub.on("model-id-updated", updateRouterOnModelIdChange);
onBeforeUnmount(() => {
  eventHub.off("model-id-updated", updateRouterOnModelIdChange);
});

/**
 * Update the router path when the model id configuration changes.
 * Triggered from "model" store module
 */
function updateRouterOnModelIdChange(
  id: number | string,
  oldId: number | string
) {
  const route = router.currentRoute.value;

  // move to editor url from publications url when saving,
  // because we are moving context - we're not viewing a single
  // publication anymore, we are editing the MODEL
  if (route.fullPath.includes("/publications/")) {
    console.log(`Model id changed to ${id} from ${oldId}`);
    router.push({
      path: `/models/${id}`
    });
    return false;
  }

  const params = route.params;

  // Only update the url if the model id in the URL is a
  // different one than the id of the currently loaded model
  if (params.id == oldId) {
    router.push({
      ...route,
      params: {
        ...params,
        id: id
      }
    });
  }
}
</script>

<style lang="scss">
.app-editor {
  height: 100vh;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content minmax(0, 1fr);

  -webkit-user-select: none;
  user-select: none;

  &__navigation {
    z-index: 12;
  }

  &__view {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}
</style>
